import React, { useState, useEffect, useRef, useMemo } from "react";
import Box from "@mui/material/Box";
import SignoutHeader from "../subcomponents/SignoutHeader";
import Typography from "@mui/material/Typography";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import {
  ActivePlanOfCare,
  AgentType,
  AppointmentCategory,
  AppointmentType,
  Consent,
  EHR,
  LateCancellationFee,
  PartialOmniPatient,
  StreamType,
  TextThreadMessage,
  TextWebsocketEvents,
  WaitlistRun,
  WaitlistSlot,
  WaitlistSlotStateChange,
  WebsocketActions,
} from "../types";
import { Button, IconButton, CircularProgress, Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Popover, Select, MenuItem, Checkbox, ListItemText, TextField } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Sidebar } from "../styles/GeneralStyles";
import { RootState, useAppDispatch } from "../store";
import { useSelector } from "react-redux";
import { Colors } from "../Colors";
import { api, convertE164ToReadable, formatIsoToCustomDateStringWithEEEE, formatIsoToCustomDateStringWithEEEEHHMMAWithoutTimezone, formatIsoUsingOn } from "../utils/utils";
import { isDateRange, filterPastAvailableDates } from "../utils/availableDates";
import { getWaitlistPatient, archivePatient, updatePatient as updatePatientThunk } from "../slices/PatientSlice";
import { DateTime } from "luxon";
import { Link, useNavigate, useParams } from "react-router-dom";
import TextThreadDisplay from "../components/TextThreadDisplay";
import { PatientStatusAvatarTooltip, statusMessages } from "./WaitlistRunInfo";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { updateWaitlistSlot } from "../slices/WaitlistSlotsSlice";
import { SecondaryButton } from "../subcomponents/CustomButton";
import WarningIcon from "@mui/icons-material/Warning";
import { UpdateWebptPatientModal } from "../forms/UpdatePatientModal";
import { useSnackbar } from "../providers/SnackbarProvider";
import axios from "axios";
import { PlusIcon } from "lucide-react";
import { Add, ArrowOutwardOutlined, CreditCard, MedicalServices } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import BugReportIcon from "@mui/icons-material/BugReport";
import { fetchProviderGroups } from "../slices/ProviderSlice";
import { fetchWebptProviders } from "../slices/WebptIntegrationSlice";
import { fetchInsurances } from "../slices/InsurancesSlice";
import { AutoComplete } from "../subcomponents/AutoComplete";
import { filterAppointmentTypesForPicker } from "../utils/appointmentTypes";

// The unique booking-link token isn't stored anywhere on the patient/plan-of-care
// records — it's only ever delivered via text. This recovers the most recently
// texted link (if any) by scanning the thread newest-first.
const PLAN_OF_CARE_BOOKING_LINK_REGEX = /\/patient\/book-plan-of-care-visit\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/;

const languageOptions = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
];

const PatientInfoPage: React.FC = () => {
  const patientId = useParams<{ patientId: string }>().patientId || "";
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const loadingPatients = useSelector((state: RootState) => state.patients.loadingFetchPatients);

  const patients = useSelector((state: RootState) => state.patients.patients);
  const patient = patients.find((p) => p.patientId === patientId);
  const ehrId = useMemo(() => patient?.[EHR.webpt]?.webptPatientId, [patient]);
  const caseStudyId = useMemo(() => patient?.[EHR.webpt]?.webptCaseStudyId, [patient]);

  // Provider & Provider Group selectors
  const webptProviders = useSelector((state: RootState) => state.webptIntegration.providers);
  const providerGroups = useSelector((state: RootState) => state.providers.providerGroups);

  const providerNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    webptProviders.forEach((p) => {
      map[p.id] = p.name;
    });
    return map;
  }, [webptProviders]);

  const providerGroupNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    providerGroups.forEach((g) => {
      map[g.providerGroupId] = g.name;
    });
    return map;
  }, [providerGroups]);

  const providerOptions = useMemo(() => webptProviders.map((p) => ({ value: p.id, label: p.name })), [webptProviders]);
  const providerGroupOptions = useMemo(() => providerGroups.map((g) => ({ value: g.providerGroupId, label: g.name })), [providerGroups]);

  const [contactMode, setContactMode] = useState<"text" | "call" | "both">(patient?.patient.textEnabled ? (patient?.patient.callEnabled ? "both" : "text") : "call");
  const [language, setLanguage] = useState<string>(patient?.patient.language || "en");

  // Insurance selector & state
  const { insurances } = useSelector((state: RootState) => state.insurances);
  const [editingInsurance, setEditingInsurance] = useState<boolean>(false);
  const [selectedInsurance, setSelectedInsurance] = useState<{ label: string; value: string } | null>(null);
  const insuranceOptions = useMemo(() => insurances.map((ins) => ({ label: ins.name, value: ins.insuranceId })), [insurances]);

  // Initial eval appointment type state
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [editingEvalType, setEditingEvalType] = useState<boolean>(false);
  const [selectedEvalType, setSelectedEvalType] = useState<{ label: string; value: string } | null>(null);
  const evalTypeOptions = useMemo(
    () =>
      filterAppointmentTypesForPicker(
        appointmentTypes.filter((t) => t.category === AppointmentCategory.evaluation || t.webptShortCode === "IE" || t.name === "Initial Examination"),
        patient?.[EHR.webpt]?.initialEvalAppointmentTypeId,
        organization?.organizationId,
      ).map((t) => ({ label: t.label || t.name, value: t.appointmentTypeId })),
    [appointmentTypes, patient, organization?.organizationId],
  );

  // Provider popover editing state
  const [providerPopoverAnchor, setProviderPopoverAnchor] = useState<HTMLElement | null>(null);
  const [editingPocId, setEditingPocId] = useState<string | null>(null);
  const [editProviderGroupId, setEditProviderGroupId] = useState<string>("");
  const [editSelectedProviders, setEditSelectedProviders] = useState<string[]>([]);
  const [savingProviders, setSavingProviders] = useState<boolean>(false);

  // Frequency popover editing state
  const [frequencyPopoverAnchor, setFrequencyPopoverAnchor] = useState<HTMLElement | null>(null);
  const [editingFrequencyPocId, setEditingFrequencyPocId] = useState<string | null>(null);
  const [editFrequencyValue, setEditFrequencyValue] = useState<number>(0);
  const [savingFrequency, setSavingFrequency] = useState<boolean>(false);

  const openProviderPopover = (event: React.MouseEvent<HTMLElement>, planOfCare: ActivePlanOfCare) => {
    event.preventDefault();
    event.stopPropagation();
    setProviderPopoverAnchor(event.currentTarget);
    setEditingPocId(planOfCare.activePlanOfCareId);
    setEditProviderGroupId(planOfCare.providerGroupId || "");
    setEditSelectedProviders(planOfCare.webptProviderIds || []);
  };

  const closeProviderPopover = () => {
    setProviderPopoverAnchor(null);
    setEditingPocId(null);
  };

  const handleEditProviderGroupChange = (newGroupId: string) => {
    setEditProviderGroupId(newGroupId);
    if (newGroupId) {
      const group = providerGroups.find((g) => g.providerGroupId === newGroupId);
      if (group) {
        setEditSelectedProviders(group.providerIds);
      }
    }
  };

  const saveProviderAssignment = async () => {
    if (!user?.token || !editingPocId) return;
    setSavingProviders(true);
    try {
      await api.patch(`/plan-of-care/active/${editingPocId}`, user.token, {
        webptProviderIds: editSelectedProviders,
        providerGroupId: editProviderGroupId || null,
      });
      closeProviderPopover();
      await fetchPlansOfCare();
      showMessage("Provider assignment updated", "success");
    } catch (error) {
      console.error("Error updating provider assignment:", error);
      showMessage("Error updating provider assignment", "error");
    } finally {
      setSavingProviders(false);
    }
  };

  const openFrequencyPopover = (event: React.MouseEvent<HTMLElement>, planOfCare: ActivePlanOfCare) => {
    event.preventDefault();
    event.stopPropagation();
    setFrequencyPopoverAnchor(event.currentTarget);
    setEditingFrequencyPocId(planOfCare.activePlanOfCareId);
    setEditFrequencyValue(planOfCare.frequency || 0);
  };

  const closeFrequencyPopover = () => {
    setFrequencyPopoverAnchor(null);
    setEditingFrequencyPocId(null);
  };

  const saveFrequency = async () => {
    if (!user?.token || !editingFrequencyPocId) return;
    setSavingFrequency(true);
    try {
      await api.post(`/plan-of-care/active/${editingFrequencyPocId}/set-frequency`, user.token, {
        frequency: editFrequencyValue,
      });
      closeFrequencyPopover();
      await fetchPlansOfCare();
      showMessage("Frequency updated", "success");
    } catch (error) {
      console.error("Error updating frequency:", error);
      showMessage("Error updating frequency", "error");
    } finally {
      setSavingFrequency(false);
    }
  };

  const [textThread, setTextThread] = React.useState<TextThreadMessage[]>([]);
  const [waitlistSlotStateChanges, setWaitlistSlotStateChanges] = useState<WaitlistSlotStateChange[]>([]);
  const [slots, setSlots] = useState<(WaitlistSlot & { waitlistRun: WaitlistRun })[]>([]);
  const [calls, setCalls] = useState<any[]>([]);

  const planOfCareBookingUniqueId = useMemo(() => {
    const sortedByRecency = [...textThread].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    for (const message of sortedByRecency) {
      const match = message.message?.match(PLAN_OF_CARE_BOOKING_LINK_REGEX);
      if (match) return match[1];
    }
    return undefined;
  }, [textThread]);

  const openPlanOfCareBookingPage = (debug: boolean) => {
    if (!planOfCareBookingUniqueId) return;
    const path = `/patient/book-plan-of-care-visit/${planOfCareBookingUniqueId}${debug ? "?debug=true" : ""}`;
    window.open(path, "_blank", "noopener,noreferrer");
  };

  const [textThreadLoading, setTextThreadLoading] = React.useState<boolean>(true);
  const [waitlistSlotStateChangesLoading, setWaitlistSlotStateChangesLoading] = useState<boolean>(false);
  const [slotsLoading, setSlotsLoading] = React.useState<boolean>(true);
  const [callsLoading, setCallsLoading] = React.useState<boolean>(true);

  const [activePlansOfCare, setActivePlansOfCare] = useState<ActivePlanOfCare[]>([]);
  const [loadingPlansOfCare, setLoadingPlansOfCare] = useState<boolean>(true);
  const [caseStudies, setCaseStudies] = useState<{ id: number; name: string; isActive: boolean }[]>([]);
  const [loadingCaseStudies, setLoadingCaseStudies] = useState<boolean>(true);
  const [sendingInitialVisit, setSendingInitialVisit] = useState<Record<string, boolean>>({});
  const [sendingPocLink, setSendingPocLink] = useState<Record<string, boolean>>({});
  const [showAllAvailableDates, setShowAllAvailableDates] = useState<boolean>(false);
  const [showAllSlots, setShowAllSlots] = useState<boolean>(false);
  const [showAllPayments, setShowAllPayments] = useState<boolean>(false);
  const [rightColumnHeight, setRightColumnHeight] = useState<number | null>(null);
  const leftSidebarRef = useRef<HTMLDivElement | null>(null);
  const [showNoBookableVisitsDialog, setShowNoBookableVisitsDialog] = useState<boolean>(false);
  const [pendingPlanOfCareId, setPendingPlanOfCareId] = useState<string | null>(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState<boolean>(false);
  const [archivingPatient, setArchivingPatient] = useState<boolean>(false);

  const [lateCancellationFees, setLateCancellationFees] = useState<LateCancellationFee[]>([]);
  const [loadingLateCancellationFees, setLoadingLateCancellationFees] = useState<boolean>(true);

  const updatePhoneNumber = async (newPhoneNumber: string) => {
    if (user?.token) {
      try {
        await api.patch(`/patients/${patientId}/phone-number`, user.token, { phoneNumber: newPhoneNumber });
        fetchPatient();
        showMessage("Successfully updated phone number", "success");
      } catch (error: any) {
        console.error("Error updating phone number:", error);

        // If the API helper returns Axios-like errors:
        if (error.response?.status === 500) {
          showMessage("Unknown server error updating phone number.", "error");
        } else if (error.response?.data?.error) {
          showMessage(`Error updating phone number: ${error.response.data.error}`, "error");
        } else {
          showMessage("Unknown error updating phone number.", "error");
        }
      }
    }
  };

  const updatePatientName = async (firstName: string, lastName: string) => {
    if (user?.token) {
      try {
        await api.patch(`/patients/${patientId}`, user.token, { firstName, lastName });
        fetchPatient();
        showMessage("Successfully updated patient name", "success");
      } catch (error: any) {
        console.error("Error updating patient name:", error);
        if (error.response?.status === 500) {
          showMessage("Unknown server error updating patient name.", "error");
        } else if (error.response?.data?.error) {
          showMessage(`Error updating patient name: ${error.response.data.error}`, "error");
        } else {
          showMessage("Unknown error updating patient name.", "error");
        }
      }
    }
  };

  const updatePreferredName = async (preferredName: string) => {
    if (user?.token) {
      try {
        await api.patch(`/patients/${patientId}`, user.token, { preferredName });
        fetchPatient();
        showMessage("Successfully updated preferred name", "success");
      } catch (error: any) {
        console.error("Error updating preferred name:", error);
        if (error.response?.status === 500) {
          showMessage("Unknown server error updating preferred name.", "error");
        } else if (error.response?.data?.error) {
          showMessage(`Error updating preferred name: ${error.response.data.error}`, "error");
        } else {
          showMessage("Unknown error updating preferred name.", "error");
        }
      }
    }
  };

  const { showMessage } = useSnackbar();

  const handleContactModeChange = async (mode: "text" | "call" | "both") => {
    setContactMode(mode);
    if (user?.token) {
      await api.patch(`/patients/${patientId}`, user.token, {
        textEnabled: mode === "text" || mode === "both",
        callEnabled: mode === "call" || mode === "both",
      });
      fetchPatient();
    }
  };

  const handleLanguageChange = async (newLanguage: string) => {
    setLanguage(newLanguage);
    if (user?.token) {
      try {
        await api.patch(`/patients/${patientId}`, user.token, { language: newLanguage });
        fetchPatient();
        showMessage("Successfully updated language", "success");
      } catch (error) {
        console.error("Error updating language:", error);
        showMessage("Error updating language", "error");
      }
    }
  };

  const [newPhoneNumber, setNewPhoneNumber] = useState<string>("");
  const [editingPhoneNumber, setEditingPhoneNumber] = useState<boolean>(false);

  const [newFirstName, setNewFirstName] = useState<string>("");
  const [newLastName, setNewLastName] = useState<string>("");
  const [editingName, setEditingName] = useState<boolean>(false);

  const [newPreferredName, setNewPreferredName] = useState<string>("");
  const [editingPreferredName, setEditingPreferredName] = useState<boolean>(false);

  const handleEditPhoneNumber = () => {
    if (patient) {
      setNewPhoneNumber(patient.patient.phoneNumber);
      setEditingPhoneNumber(true);
    }
  };

  const handleSubmitPhoneNumber = () => {
    updatePhoneNumber(newPhoneNumber);
    setEditingPhoneNumber(false);
  };

  const handleCancelPhoneNumber = () => {
    setEditingPhoneNumber(false);
    if (patient) {
      setNewPhoneNumber(patient.patient.phoneNumber);
    }
  };

  const handleEditName = () => {
    if (patient) {
      setNewFirstName(patient.patient.firstName);
      setNewLastName(patient.patient.lastName);
      setEditingName(true);
    }
  };

  const handleSubmitName = () => {
    updatePatientName(newFirstName, newLastName);
    setEditingName(false);
  };

  const handleCancelName = () => {
    setEditingName(false);
    if (patient) {
      setNewFirstName(patient.patient.firstName);
      setNewLastName(patient.patient.lastName);
    }
  };

  const handleEditPreferredName = () => {
    if (patient) {
      setNewPreferredName(patient.patient.preferredName || "");
      setEditingPreferredName(true);
    }
  };

  const handleSubmitPreferredName = () => {
    updatePreferredName(newPreferredName);
    setEditingPreferredName(false);
  };

  const handleCancelPreferredName = () => {
    setEditingPreferredName(false);
    if (patient) {
      setNewPreferredName(patient.patient.preferredName || "");
    }
  };

  const updateInsurance = async (insuranceId: string | undefined) => {
    if (!user?.token || !patient) return;
    try {
      const omniPatient: PartialOmniPatient = {
        patientId,
        patient: { patientId, insuranceId },
        waitlist: { patientId },
      };
      await dispatch(updatePatientThunk({ token: user.token, patient: omniPatient })).unwrap();
      showMessage("Successfully updated insurance", "success");
    } catch (error: any) {
      console.error("Error updating insurance:", error);
      showMessage(`Error updating insurance: ${error?.message || "Unknown error"}`, "error");
    }
  };

  const handleSubmitInsurance = () => {
    updateInsurance(selectedInsurance?.value);
    setEditingInsurance(false);
  };

  const handleCancelInsurance = () => {
    setEditingInsurance(false);
    if (patient && insuranceOptions.length > 0) {
      const insurance = insuranceOptions.find((ins) => ins.value === patient.patient.insuranceId);
      setSelectedInsurance(insurance || null);
    }
  };

  const refreshWaitlistEligibility = async () => {
    if (!user?.token || !patient) return;
    try {
      const omniPatient: PartialOmniPatient = {
        patientId,
        patient: { patientId, lastRespondedAt: DateTime.now().toISO(), waitlistPausedNotifiedAt: null },
        waitlist: { patientId },
      };
      await dispatch(updatePatientThunk({ token: user.token, patient: omniPatient })).unwrap();
      showMessage("Patient will now receive waitlist texts again", "success");
    } catch (error: any) {
      console.error("Error refreshing waitlist eligibility:", error);
      showMessage(`Error refreshing waitlist eligibility: ${error?.message || "Unknown error"}`, "error");
    }
  };

  const updateInitialEvalAppointmentType = async (appointmentTypeId: string | null) => {
    if (!user?.token || !patient) return;
    try {
      const omniPatient: PartialOmniPatient = {
        patientId,
        patient: { patientId },
        waitlist: { patientId },
        // null (not undefined) clears the field — the backend drops undefined keys on update
        [EHR.webpt]: { patientId, initialEvalAppointmentTypeId: appointmentTypeId },
      };
      await dispatch(updatePatientThunk({ token: user.token, patient: omniPatient })).unwrap();
      showMessage("Successfully updated initial eval appointment type", "success");
    } catch (error: any) {
      console.error("Error updating initial eval appointment type:", error);
      showMessage(`Error updating initial eval appointment type: ${error?.message || "Unknown error"}`, "error");
    }
  };

  const handleSubmitEvalType = () => {
    updateInitialEvalAppointmentType(selectedEvalType?.value ?? null);
    setEditingEvalType(false);
  };

  const handleCancelEvalType = () => {
    setEditingEvalType(false);
    const storedEvalTypeId = patient?.[EHR.webpt]?.initialEvalAppointmentTypeId;
    setSelectedEvalType(storedEvalTypeId ? evalTypeOptions.find((opt) => opt.value === storedEvalTypeId) || null : null);
  };

  const caseStudyIdsWithPlanOfCare = useMemo(
    () => new Set(activePlansOfCare.map((plan) => plan.caseStudyId).filter((id): id is number => id != null)),
    [activePlansOfCare],
  );

  const handleSendInitialVisit = async (caseStudyId: number) => {
    if (!user?.token) return;

    const caseStudyKey = caseStudyId.toString();
    setSendingInitialVisit((prev) => ({ ...prev, [caseStudyKey]: true }));
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/initial-visit/send`,
        {
          patientId: patientId,
          caseStudyId: caseStudyId,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        },
      );
      showMessage("Successfully sent link to book initial visit", "success");
    } catch (error) {
      showMessage("Error sending link to book initial visit", "error");
    } finally {
      setSendingInitialVisit((prev) => ({ ...prev, [caseStudyKey]: false }));
    }
  };

  const sendPocBookingLink = async (activePlanOfCareId: string) => {
    if (!user?.token) return;

    setSendingPocLink((prev) => ({ ...prev, [activePlanOfCareId]: true }));
    try {
      await api.post(`/bookings/plan-of-care/send`, user.token, {
        patientId: patientId,
        organizationId: user.organizationId,
        planOfCareId: activePlanOfCareId,
        type: "update",
      });
      showMessage("Successfully sent booking link", "success");
    } catch (error) {
      showMessage("Error sending booking link", "error");
    } finally {
      setSendingPocLink((prev) => ({ ...prev, [activePlanOfCareId]: false }));
    }
  };

  const handleSendPocBookingLink = (activePlanOfCareId: string) => {
    // Find the plan of care to check unscheduled appointments
    const planOfCare = activePlansOfCare.find((poc) => poc.activePlanOfCareId === activePlanOfCareId);

    // Check if there are no unscheduled visits
    if (planOfCare && planOfCare.appointmentsUnscheduled === 0) {
      setPendingPlanOfCareId(activePlanOfCareId);
      setShowNoBookableVisitsDialog(true);
    } else {
      sendPocBookingLink(activePlanOfCareId);
    }
  };

  const handleConfirmSendBookingLink = () => {
    if (pendingPlanOfCareId) {
      sendPocBookingLink(pendingPlanOfCareId);
    }
    setShowNoBookableVisitsDialog(false);
    setPendingPlanOfCareId(null);
  };

  const handleCancelSendBookingLink = () => {
    setShowNoBookableVisitsDialog(false);
    setPendingPlanOfCareId(null);
  };

  const handleArchivePatient = async () => {
    if (!user?.token || !patientId) return;

    setArchivingPatient(true);
    try {
      await dispatch(archivePatient({ token: user.token, patientId }));
      showMessage("Patient archived successfully", "success");
      navigate("/dashboard/patients");
    } catch (error: any) {
      console.error("Error archiving patient:", error);
      showMessage("Failed to archive patient", "error");
    } finally {
      setArchivingPatient(false);
      setShowArchiveDialog(false);
    }
  };

  const [updateOpen, setUpdateOpen] = useState<boolean>(false);

  const socketRef = useRef<WebSocket | null>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (user?.token && !insurances.length) {
      dispatch(fetchInsurances(user.token));
    }
  }, [dispatch, user, insurances]);

  useEffect(() => {
    if (patient) {
      const p = patient.patient;
      const mode = p.textEnabled ? (p.callEnabled ? "both" : "text") : "call";
      setContactMode(mode);
      setLanguage(p.language || "en");
    }
  }, [patient]);

  useEffect(() => {
    if (patient && insuranceOptions.length > 0) {
      if (patient.patient.insuranceId) {
        const insurance = insuranceOptions.find((ins) => ins.value === patient.patient.insuranceId);
        setSelectedInsurance(insurance || null);
      } else {
        setSelectedInsurance(null);
      }
    }
  }, [patient, insuranceOptions]);

  useEffect(() => {
    const fetchAppointmentTypes = async () => {
      if (!user?.token || !organization?.organizationId) {
        return;
      }
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/appointment-types`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: {
            organizationId: organization.organizationId,
          },
        });
        setAppointmentTypes(response.data);
      } catch (error) {
        console.error("Error fetching appointment types:", error);
      }
    };
    fetchAppointmentTypes();
  }, [user?.token, organization?.organizationId]);

  useEffect(() => {
    const storedEvalTypeId = patient?.[EHR.webpt]?.initialEvalAppointmentTypeId;
    setSelectedEvalType(storedEvalTypeId ? evalTypeOptions.find((opt) => opt.value === storedEvalTypeId) || null : null);
  }, [patient, evalTypeOptions]);

  const cardStyles = React.useMemo(
    () => ({
      backgroundColor: Colors.bg2,
      borderRadius: "12px",
      padding: "24px",
      border: `1px solid ${Colors.secondaryDark}`,
    }),
    [],
  );
  const interactiveRowStyles = React.useMemo(
    () => ({
      borderRadius: "10px",
      border: `1px solid rgba(255, 255, 255, 0.12)`,
      backgroundColor: "rgba(255, 255, 255, 0.04)",
      padding: "14px 16px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
    }),
    [],
  );
  const sortedSlots = React.useMemo(() => {
    if (!slots || slots.length === 0) {
      return [];
    }

    return [...slots].sort((a, b) => DateTime.fromISO(b.waitlistRun.appointmentDate).toMillis() - DateTime.fromISO(a.waitlistRun.appointmentDate).toMillis());
  }, [slots]);
  const formattedAvailableDates = React.useMemo(() => {
    if (!patient) {
      return [];
    }

    return filterPastAvailableDates(patient.waitlist.availableDates).map((date) => {
      if (typeof date === "string") return formatIsoToCustomDateStringWithEEEE(date);
      if (isDateRange(date)) return `${formatIsoToCustomDateStringWithEEEE(date.startDate)} – ${formatIsoToCustomDateStringWithEEEE(date.endDate)}`;
      return formatIsoToCustomDateStringWithEEEE(date.start);
    });
  }, [patient]);
  const maxVisibleAvailableDates = 4;
  const displayedAvailableDates = React.useMemo(
    () => (showAllAvailableDates ? formattedAvailableDates : formattedAvailableDates.slice(0, maxVisibleAvailableDates)),
    [formattedAvailableDates, showAllAvailableDates],
  );
  const hiddenAvailableDatesCount = React.useMemo(() => Math.max(formattedAvailableDates.length - maxVisibleAvailableDates, 0), [formattedAvailableDates]);
  const maxVisibleSlots = 3;
  const displayedSlots = React.useMemo(() => (showAllSlots ? sortedSlots : sortedSlots.slice(0, maxVisibleSlots)), [sortedSlots, showAllSlots]);
  const hiddenSlotsCount = React.useMemo(() => Math.max(sortedSlots.length - maxVisibleSlots, 0), [sortedSlots]);
  const maxVisiblePayments = 3;
  const displayedPayments = React.useMemo(() => (showAllPayments ? lateCancellationFees : lateCancellationFees.slice(0, maxVisiblePayments)), [lateCancellationFees, showAllPayments]);
  const hiddenPaymentsCount = React.useMemo(() => Math.max(lateCancellationFees.length - maxVisiblePayments, 0), [lateCancellationFees]);

  const fetchPatient = async () => {
    if (user?.token && patientId) {
      dispatch(getWaitlistPatient({ token: user.token, patientId }));
    }
  };

  const fetchTextThread = async () => {
    setTextThreadLoading(true);
    if (user?.token) {
      const response = await api.get(`/patients/${patientId}/texts`, user.token);
      setTextThread(response.data);
    }
    setTextThreadLoading(false);
  };

  const fetchWaitlistSlotStateChanges = async () => {
    setWaitlistSlotStateChangesLoading(true);
    if (user?.token) {
      const response = await api.get(`/patients/${patientId}/waitlist-slot-state-changes`, user.token);
      setWaitlistSlotStateChanges(response.data);
    }
    setWaitlistSlotStateChangesLoading(false);
  };

  const fetchPlansOfCare = async () => {
    setLoadingPlansOfCare(true);
    if (user?.token && organization?.integrations.includes(EHR.webpt)) {
      const response = await api.get(`/plan-of-care/patient/${patientId}/brief`, user.token);
      setActivePlansOfCare(response.data);
    }
    setLoadingPlansOfCare(false);
  };

  const fetchCaseStudies = async () => {
    if (user?.token && organization?.integrations.includes(EHR.webpt) && ehrId) {
      setLoadingCaseStudies(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/webpt/patients/${ehrId}`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        const data = response.data;
        setCaseStudies(data.caseStudies || []);
      } catch (error) {
        console.error("Error fetching case studies:", error);
        setCaseStudies([]);
      } finally {
        setLoadingCaseStudies(false);
      }
    } else {
      setLoadingCaseStudies(false);
    }
  };

  const deletePatientPermanently = async () => {
    if (user?.token) {
      await api.delete(`/patients/${patientId}/permanent`, user.token);
    }
  };

  useEffect(() => {
    if (!user || !user?.userId || !user?.token) {
      return;
    }

    // Check if a WebSocket connection already exists
    if (socketRef.current && (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING)) {
      console.log("WebSocket connection is already open or connecting.");
      return;
    }

    const connectWebSocket = async () => {
      try {
        if (!user?.token) {
          return;
        }
        const socket = new WebSocket(`wss://${process.env.REACT_APP_BACKEND_URL_NO_HTTPS}/api/text-subscriptions/${user.userId}?token=${encodeURIComponent(user.token)}`);
        socketRef.current = socket;

        socket.onopen = async () => {
          // Add a delay to ensure the WebSocket connection is ready before subscribing
          // TODO (Hizami) - Figure out why the websocket connection is not immediately ready
          await new Promise((resolve) => setTimeout(resolve, 1000));
          console.log("Connected to WebSocket");
          socket.send(
            JSON.stringify({
              action: WebsocketActions.subscribe,
              type: TextWebsocketEvents.textThreadMessages,
            }),
          );
          socket.send(
            JSON.stringify({
              action: WebsocketActions.subscribe,
              type: TextWebsocketEvents.waitlistSlotStateChanges,
            }),
          );
        };

        socket.onmessage = (event: MessageEvent<string>) => {
          const message: {
            eventType: TextWebsocketEvents;
            streamType: StreamType;
            data: TextThreadMessage | WaitlistSlotStateChange;
          } = JSON.parse(event.data) as {
            eventType: TextWebsocketEvents;
            streamType: StreamType;
            data: TextThreadMessage | WaitlistSlotStateChange;
          };

          switch (message.eventType) {
            case TextWebsocketEvents.textThreadMessages:
              switch (message.streamType) {
                case StreamType.INSERT: {
                  const textThreadMessage: TextThreadMessage = message.data as TextThreadMessage;
                  if (textThreadMessage.patientId === patientId && !textThread?.find((msg) => msg.textThreadMessageId === textThreadMessage.textThreadMessageId)) {
                    setTextThread((prevTextThread: TextThreadMessage[]) => [...prevTextThread, textThreadMessage]);
                  }
                  break;
                }
              }
              break;
            case TextWebsocketEvents.waitlistSlotStateChanges:
              switch (message.streamType) {
                case StreamType.INSERT: {
                  const waitlistSlotStateChange: WaitlistSlotStateChange = message.data as WaitlistSlotStateChange;
                  console.log("Received waitlist slot state change message:", waitlistSlotStateChange);
                  dispatch(
                    updateWaitlistSlot({
                      waitlistSlotId: waitlistSlotStateChange.waitlistSlotId,
                      state: waitlistSlotStateChange.state,
                      stateJustification: waitlistSlotStateChange.stateJustification,
                    }),
                  );
                  break;
                }
              }
              break;
            default:
              console.log("Received unknown message:", message);
              break;
          }
        };

        socket.onclose = () => {
          console.log("Disconnected from WebSocket");
          socketRef.current = null;
        };

        socket.onerror = (error) => {
          console.error("WebSocket error:", error);
          socket.close();
        };

        return () => {
          socket.close();
        };
      } catch (error) {
        console.error("Error connecting to WebSocket:", error);
      }
    };

    connectWebSocket();

    // Clean up the WebSocket connection when the component unmounts or user changes
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [user?.token]);

  const fetchSlots = async () => {
    setSlotsLoading(true);
    if (user?.token) {
      const response = await api.get(`/patients/${patientId}/slots`, user.token);
      setSlots(response.data);
    }
    setSlotsLoading(false);
  };

  const fetchCalls = async () => {
    setCallsLoading(true);
    if (user?.token) {
      const response = await api.get(`/patients/${patientId}/calls`, user.token);
      setCalls(response.data);
    }
    setCallsLoading(false);
  };

  const fetchLateCancellationFees = async () => {
    setLoadingLateCancellationFees(true);
    if (user?.token) {
      try {
        const response = await api.get(`/stripe-connect/late-cancellation-fees/${patientId}`, user.token);
        setLateCancellationFees(response.data.fees || []);
      } catch (error) {
        console.error("Error fetching late cancellation fees:", error);
        setLateCancellationFees([]);
      }
    }
    setLoadingLateCancellationFees(false);
  };

  useEffect(() => {
    fetchTextThread();
    fetchSlots();
    fetchCalls();
    fetchWaitlistSlotStateChanges();
    fetchPlansOfCare();
    fetchCaseStudies();
    fetchLateCancellationFees();
  }, [user?.token, patientId, ehrId]);

  useEffect(() => {
    fetchPatient();
  }, [user?.token]);

  // Fetch providers and provider groups for POC cards
  useEffect(() => {
    if (user?.token && organization?.organizationId) {
      dispatch(fetchWebptProviders({ token: user.token, organizationId: organization.organizationId }));
      dispatch(fetchProviderGroups(user.token));
    }
  }, [user?.token, organization?.organizationId]);

  useEffect(() => {
    setShowAllAvailableDates(false);
    setShowAllSlots(false);
    setShowAllPayments(false);
  }, [patientId]);
  useEffect(() => {
    if (formattedAvailableDates.length <= maxVisibleAvailableDates && showAllAvailableDates) {
      setShowAllAvailableDates(false);
    }
  }, [formattedAvailableDates, maxVisibleAvailableDates, showAllAvailableDates]);
  useEffect(() => {
    if (sortedSlots.length <= maxVisibleSlots && showAllSlots) {
      setShowAllSlots(false);
    }
  }, [sortedSlots, maxVisibleSlots, showAllSlots]);
  useEffect(() => {
    if (lateCancellationFees.length <= maxVisiblePayments && showAllPayments) {
      setShowAllPayments(false);
    }
  }, [lateCancellationFees, maxVisiblePayments, showAllPayments]);

  useEffect(() => {
    const syncRightColumnHeight = () => {
      if (window.innerWidth < 1200) {
        setRightColumnHeight(null);
        return;
      }

      const leftSidebarHeight = leftSidebarRef.current?.getBoundingClientRect().height;
      if (!leftSidebarHeight) return;

      const roundedHeight = Math.ceil(leftSidebarHeight);
      setRightColumnHeight((prev) => (prev === roundedHeight ? prev : roundedHeight));
    };

    syncRightColumnHeight();

    const resizeObserver = new ResizeObserver(syncRightColumnHeight);
    if (leftSidebarRef.current) {
      resizeObserver.observe(leftSidebarRef.current);
    }

    window.addEventListener("resize", syncRightColumnHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncRightColumnHeight);
    };
  }, [patientId]);

  const navigate = useNavigate();

  const agents = useSelector((state: RootState) => state.agents.agents);
  // TODO (Hizami): This is hardcoded as waitlist type since only waitlist agents use this page for now
  const agent = agents.find((agent) => agent.agentType === AgentType.waitlist);
  const agentId = agent?.agentId || "";

  const consentHistory = patient?.patient.consentHistory || [
    {
      consent: Consent.given,
      message: "Consent given",
      date: DateTime.now().toISO(),
    },
  ];
  const latestConsent = consentHistory[consentHistory.length - 1];
  const isRevoked = latestConsent.consent === Consent.revoked;
  const lastConsentMessage = latestConsent.message;
  const lastConsentDate = DateTime.fromISO(latestConsent.date).toLocaleString(DateTime.DATE_MED);
  const revokedMessage = `This patient has placed themselves on the Do Not Call list via the request "${lastConsentMessage}" on ${lastConsentDate}`;

  const integration = organization?.integrations.includes(EHR.webpt) ? EHR.webpt : EHR.none;
  const hasWebptIntegration = integration === EHR.webpt;

  return (
    <Box
      sx={{
        marginTop: { xs: "75px", sm: 0, color: "#FFF" },
        maxWidth: "1400px",
        margin: "0 auto",
        padding: { xs: "0 16px", sm: "0 24px" },
      }}
    >
      <SignoutHeader />
      {loadingPatients && !patient && <LoadingWithMessage message="Loading patients..." size={20} customStyles={{ marginTop: "20px" }} />}
      {patient && (
        <Box>
          {/* Header with Back Button */}
          <Box>
            <Link to="/dashboard/patients" style={{ textDecoration: "none" }}>
              <SecondaryButton
                sx={{
                  display: { xs: "none", lg: "flex" },
                  alignItems: "center",
                  marginRight: "16px",
                }}
              >
                <ArrowBackIcon sx={{ marginRight: "10px" }} /> Back
              </SecondaryButton>
            </Link>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                mb: 3,
              }}
            >
              <Typography variant="h4" fontWeight="700">
                Patient Details
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => setShowArchiveDialog(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Archive Patient
              </Button>
            </Box>
          </Box>

          {/* Alert Banner for Revoked Consent */}
          {isRevoked && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "16px",
                marginBottom: "24px",
                backgroundColor: "rgba(211, 47, 47, 0.1)",
                border: `2px solid ${Colors.error}`,
                borderRadius: "8px",
              }}
            >
              <WarningIcon sx={{ color: Colors.error, marginRight: "12px", fontSize: "28px" }} />
              <Typography variant="body1" sx={{ color: Colors.error }}>
                {revokedMessage}
              </Typography>
            </Box>
          )}

          {/* Main Content Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "350px 1fr" },
              alignItems: "start",
              gap: "24px",
              marginBottom: "24px",
            }}
          >
            {/* Left Sidebar */}
            <Box
              ref={leftSidebarRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              <Box sx={cardStyles}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "#FFF", fontWeight: 600 }}>
                    Patient Information
                  </Typography>
                  {hasWebptIntegration && ehrId && caseStudyId && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        cursor: "pointer",
                        color: Colors.primary,
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        "&:hover": {
                          color: Colors.primaryDark,
                        },
                      }}
                      component="a"
                      href={`https://app.webpt.com/patient/edit/index/PatientID/${ehrId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View in WebPT
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: "grid", gap: "20px", marginTop: "20px" }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: Colors.info }}>
                      Name
                    </Typography>
                    {editingName ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <Box
                          component="input"
                          type="text"
                          value={newFirstName}
                          onChange={(e) => setNewFirstName(e.target.value)}
                          placeholder="First Name"
                          sx={{
                            width: "100%",
                            marginTop: "8px",
                            padding: "10px 12px",
                            borderRadius: "8px",
                            border: `1px solid ${Colors.secondaryDark}`,
                            backgroundColor: Colors.bg3,
                            color: "#FFF",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        <Box
                          component="input"
                          type="text"
                          value={newLastName}
                          onChange={(e) => setNewLastName(e.target.value)}
                          placeholder="Last Name"
                          sx={{
                            width: "100%",
                            padding: "10px 12px",
                            borderRadius: "8px",
                            border: `1px solid ${Colors.secondaryDark}`,
                            backgroundColor: Colors.bg3,
                            color: "#FFF",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        <Box sx={{ display: "flex", gap: "8px" }}>
                          <Button
                            variant="contained"
                            disableElevation
                            color="primary"
                            size="small"
                            onClick={handleSubmitName}
                            disabled={newFirstName.trim() === "" || newLastName.trim() === ""}
                            sx={{ textTransform: "none" }}
                            fullWidth
                          >
                            Save
                          </Button>
                          <Button variant="outlined" color="error" size="small" onClick={handleCancelName} sx={{ textTransform: "none" }} fullWidth>
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Typography variant="h6" sx={{ color: "#FFF" }}>
                          {patient.patient.firstName} {patient.patient.lastName}
                        </Typography>
                        <Tooltip title="Copy name">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(`${patient.patient.firstName} ${patient.patient.lastName}`);
                              showMessage("Name copied to clipboard", "success");
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={handleEditName}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: Colors.info }}>
                      Preferred Name
                    </Typography>
                    {editingPreferredName ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <Box
                          component="input"
                          type="text"
                          value={newPreferredName}
                          onChange={(e) => setNewPreferredName(e.target.value)}
                          placeholder="Preferred Name"
                          sx={{
                            width: "100%",
                            marginTop: "8px",
                            padding: "10px 12px",
                            borderRadius: "8px",
                            border: `1px solid ${Colors.secondaryDark}`,
                            backgroundColor: Colors.bg3,
                            color: "#FFF",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        <Box sx={{ display: "flex", gap: "8px" }}>
                          <Button variant="contained" disableElevation color="primary" size="small" onClick={handleSubmitPreferredName} sx={{ textTransform: "none" }} fullWidth>
                            Save
                          </Button>
                          <Button variant="outlined" color="error" size="small" onClick={handleCancelPreferredName} sx={{ textTransform: "none" }} fullWidth>
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Typography variant="body1" sx={{ color: "#FFF", fontWeight: 600 }}>
                          {patient.patient.preferredName || "-"}
                        </Typography>
                        <IconButton size="small" onClick={handleEditPreferredName}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {ehrId && (
                    <Box>
                      <Typography variant="body2" sx={{ color: Colors.info }}>
                        Patient ID
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Typography variant="body1" sx={{ color: "#FFF", fontWeight: 600 }}>
                          {ehrId}
                        </Typography>
                        <Tooltip title="Copy Patient ID">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (ehrId !== undefined) {
                                navigator.clipboard.writeText(String(ehrId));
                                showMessage("Patient ID copied to clipboard", "success");
                              }
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" sx={{ color: Colors.info }}>
                      Phone Number
                    </Typography>
                    {editingPhoneNumber ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <Box
                          component="input"
                          type="text"
                          value={newPhoneNumber}
                          onChange={(e) => setNewPhoneNumber(e.target.value)}
                          sx={{
                            width: "100%",
                            marginTop: "8px",
                            padding: "10px 12px",
                            borderRadius: "8px",
                            border: `1px solid ${Colors.secondaryDark}`,
                            backgroundColor: Colors.bg3,
                            color: "#FFF",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                        <Box sx={{ display: "flex", gap: "8px" }}>
                          <Button
                            variant="contained"
                            disableElevation
                            color="primary"
                            size="small"
                            onClick={handleSubmitPhoneNumber}
                            disabled={newPhoneNumber.trim() === ""}
                            sx={{ textTransform: "none" }}
                            fullWidth
                          >
                            Save
                          </Button>
                          <Button variant="outlined" color="error" size="small" onClick={handleCancelPhoneNumber} sx={{ textTransform: "none" }} fullWidth>
                            Cancel
                          </Button>
                        </Box>
                        <Typography variant="caption" color={Colors.info} sx={{ fontStyle: "italic" }}>
                          Note: This will re-send the welcome text to the new phone number.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Typography variant="body1" sx={{ color: "#FFF", fontWeight: 600 }}>
                          {convertE164ToReadable(patient.patient.phoneNumber)}
                        </Typography>
                        <IconButton size="small" onClick={handleEditPhoneNumber}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {patient.patient.waitlistPausedNotifiedAt && !agent?.disableInactivityPause && (
                    <Box>
                      <Typography variant="body2" sx={{ color: Colors.info, marginBottom: "6px" }}>
                        Waitlist Texts
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Typography variant="body1" sx={{ color: Colors.warning, fontWeight: 600 }}>
                          Paused due to inactivity
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={refreshWaitlistEligibility}
                          sx={{ textTransform: "none" }}
                        >
                          Refresh
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {patient.patient.email && (
                    <Box>
                      <Typography variant="body2" sx={{ color: Colors.info, marginBottom: "6px" }}>
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#FFF", fontWeight: 600, wordBreak: "break-word" }}>
                        {patient.patient.email}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" sx={{ color: Colors.info, marginBottom: "6px" }}>
                      Preferred Contact Method
                    </Typography>
                    <Box display="flex" gap="10px">
                      {(["call", "text", "both"] as const).map((mode) => (
                        <Button
                          key={mode}
                          onClick={() => handleContactModeChange(mode)}
                          variant="outlined"
                          size="small"
                          sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            borderColor: contactMode === mode ? Colors.primary : "rgba(255, 255, 255, 0.2)",
                            backgroundColor: "transparent",
                            color: contactMode === mode ? "#fff" : Colors.info,
                          }}
                        >
                          {mode === "call" ? "Call only" : mode === "text" ? "Text only" : "Both"}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ color: Colors.info, marginBottom: "6px" }}>
                      Language
                    </Typography>
                    <Box display="flex" gap="10px">
                      {languageOptions.map((option) => (
                        <Button
                          key={option.value}
                          onClick={() => handleLanguageChange(option.value)}
                          variant="outlined"
                          size="small"
                          sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            borderColor: language === option.value ? Colors.primary : "rgba(255, 255, 255, 0.2)",
                            backgroundColor: "transparent",
                            color: language === option.value ? "#fff" : Colors.info,
                          }}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  {insuranceOptions.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ color: Colors.info }}>
                        Insurance
                      </Typography>
                      {editingInsurance ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
                          <AutoComplete label="" placeholder="Select insurance" options={insuranceOptions} value={selectedInsurance} setValue={setSelectedInsurance} />
                          <Box sx={{ display: "flex", gap: "8px" }}>
                            <Button variant="contained" disableElevation color="primary" size="small" onClick={handleSubmitInsurance} sx={{ textTransform: "none" }} fullWidth>
                              Save
                            </Button>
                            <Button variant="outlined" color="error" size="small" onClick={handleCancelInsurance} sx={{ textTransform: "none" }} fullWidth>
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <Typography variant="body1" sx={{ color: "#FFF", fontWeight: 600 }}>
                            {selectedInsurance?.label || "-"}
                          </Typography>
                          <IconButton size="small" onClick={() => setEditingInsurance(true)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {hasWebptIntegration && (
                <Box sx={cardStyles}>
                  <Typography variant="h6" sx={{ color: "#FFF", fontWeight: 600, marginBottom: "16px" }}>
                    Initial Evaluation
                  </Typography>

                  {evalTypeOptions.length > 0 && (
                    <Box sx={{ marginBottom: "16px" }}>
                      <Typography variant="body2" sx={{ color: Colors.info }}>
                        Initial Eval Appointment Type
                      </Typography>
                      {editingEvalType ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
                          <AutoComplete label="" placeholder="Default (auto-detect)" options={evalTypeOptions} value={selectedEvalType} setValue={setSelectedEvalType} />
                          <Box sx={{ display: "flex", gap: "8px" }}>
                            <Button variant="contained" disableElevation color="primary" size="small" onClick={handleSubmitEvalType} sx={{ textTransform: "none" }} fullWidth>
                              Save
                            </Button>
                            <Button variant="outlined" color="error" size="small" onClick={handleCancelEvalType} sx={{ textTransform: "none" }} fullWidth>
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <Typography variant="body1" sx={{ color: "#FFF", fontWeight: 600 }}>
                            {selectedEvalType?.label || "Default (auto-detect)"}
                          </Typography>
                          <IconButton size="small" onClick={() => setEditingEvalType(true)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )}

                  {loadingCaseStudies ? (
                    <LoadingWithMessage message="Loading case studies..." size={24} />
                  ) : caseStudies.length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {caseStudies
                        .filter((cs) => cs.isActive)
                        .map((caseStudy) => {
                          const caseStudyKey = caseStudy.id.toString();
                          const isSending = sendingInitialVisit[caseStudyKey];
                          const hasPlanOfCare = caseStudyIdsWithPlanOfCare.has(caseStudy.id);

                          return (
                            <Box
                              key={caseStudy.id}
                              sx={{
                                borderRadius: "10px",
                                border: `1px solid rgba(255, 255, 255, 0.12)`,
                                backgroundColor: "rgba(255, 255, 255, 0.04)",
                                padding: "14px 16px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                              }}
                            >
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                sx={{
                                  color: "rgba(255, 255, 255, 0.95)",
                                  lineHeight: 1.4,
                                }}
                              >
                                {caseStudy.name}
                              </Typography>

                              {hasPlanOfCare ? (
                                <Typography variant="body2" sx={{ color: Colors.info, fontStyle: "italic" }}>
                                  Initial eval already completed
                                </Typography>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  disableElevation
                                  onClick={() => handleSendInitialVisit(caseStudy.id)}
                                  disabled={isSending}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.8rem",
                                    padding: "6px 12px",
                                  }}
                                >
                                  {isSending ? "Sending..." : "Send booking link"}
                                </Button>
                              )}
                            </Box>
                          );
                        })}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: Colors.info }}>
                      No case studies available
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={cardStyles}>
                <Typography variant="h6" sx={{ color: "#FFF", fontWeight: 600 }}>
                  Waitlist
                </Typography>
                <Button variant="contained" color="primary" disableElevation fullWidth onClick={() => setUpdateOpen(true)} sx={{ marginTop: "16px", textTransform: "none", fontWeight: 600 }}>
                  Manage waitlist status
                </Button>

                <Box sx={{ marginTop: "24px" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <Typography variant="body2" sx={{ color: Colors.info }}>
                      Available Dates
                    </Typography>
                    {hiddenAvailableDatesCount > 0 && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowAllAvailableDates((prev) => !prev)}
                        sx={{
                          padding: 0,
                          minWidth: "auto",
                          textTransform: "none",
                          color: Colors.info,
                          "&:hover": { background: "transparent", textDecoration: "underline" },
                        }}
                      >
                        {showAllAvailableDates ? "Show less" : `View ${hiddenAvailableDatesCount} more`}
                      </Button>
                    )}
                  </Box>
                  {formattedAvailableDates.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {displayedAvailableDates.map((date) => (
                        <Box
                          key={date}
                          sx={{
                            borderRadius: "6px",
                            backgroundColor: Colors.bg4,
                            padding: "6px 12px",
                            fontSize: "0.875rem",
                            color: "#FFF",
                          }}
                        >
                          {date}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: Colors.info }}>
                      No availability
                    </Typography>
                  )}
                </Box>

                <Box sx={{ marginTop: "24px" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <Typography variant="body2" sx={{ color: Colors.info }}>
                      Waitlist Activity
                    </Typography>
                    {sortedSlots.length > maxVisibleSlots && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowAllSlots((prev) => !prev)}
                        sx={{
                          padding: 0,
                          minWidth: "auto",
                          textTransform: "none",
                          color: Colors.info,
                          "&:hover": { background: "transparent", textDecoration: "underline" },
                        }}
                      >
                        {showAllSlots ? "Show less" : `View ${hiddenSlotsCount} more`}
                      </Button>
                    )}
                  </Box>
                  {slotsLoading ? (
                    <LoadingWithMessage message="Loading waitlist..." size={24} />
                  ) : sortedSlots.length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {displayedSlots.map((slot) => (
                        <Box
                          key={slot.waitlistSlotId}
                          onClick={() => navigate(`/dashboard/agents/${agentId}/waitlist-runs/${slot.waitlistRunId}/slots/${slot.waitlistSlotId}`)}
                          sx={{
                            ...interactiveRowStyles,
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "12px",
                          }}
                        >
                          <PatientStatusAvatarTooltip
                            patientStatus={slot?.state}
                            sx={{
                              marginTop: "2px",
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: "rgba(255, 255, 255, 0.92)" }}>
                              {formatIsoToCustomDateStringWithEEEEHHMMAWithoutTimezone(slot.waitlistRun.appointmentDate)}
                            </Typography>
                            <Typography variant="body2" fontSize="0.8rem" color={Colors.info} sx={{ lineHeight: 1.6 }}>
                              {statusMessages[slot.state as keyof typeof statusMessages]}
                            </Typography>
                          </Box>
                          <ChevronRightIcon
                            sx={{
                              color: Colors.info,
                              fontSize: "20px",
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: Colors.info }}>
                      No waitlist activity
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Payments Card */}
              <Box sx={cardStyles}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px",
                  }}
                >
                  <Typography variant="h6" sx={{ color: "#FFF", fontWeight: 600 }}>
                    Payments
                  </Typography>
                  {hiddenPaymentsCount > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowAllPayments((prev) => !prev)}
                      sx={{
                        padding: 0,
                        minWidth: "auto",
                        textTransform: "none",
                        color: Colors.info,
                        "&:hover": { background: "transparent", textDecoration: "underline" },
                      }}
                    >
                      {showAllPayments ? "Show less" : `View ${hiddenPaymentsCount} more`}
                    </Button>
                  )}
                </Box>
                {loadingLateCancellationFees ? (
                  <LoadingWithMessage message="Loading fees..." size={24} />
                ) : lateCancellationFees.length > 0 ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {displayedPayments.map((fee) => (
                      <Box
                        key={fee.lateCancellationFeeId}
                        sx={{
                          borderRadius: "10px",
                          border: `1px solid rgba(255, 255, 255, 0.12)`,
                          backgroundColor: "rgba(255, 255, 255, 0.04)",
                          padding: "14px 16px",
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <Typography variant="body2" fontWeight={600} sx={{ color: "rgba(255, 255, 255, 0.92)" }}>
                            {formatIsoToCustomDateStringWithEEEEHHMMAWithoutTimezone(fee.cancelledAppointmentDate)}
                          </Typography>
                          {fee.amount != null && (
                            <Typography variant="body2" fontWeight={700} sx={{ color: Colors.success }}>
                              ${(fee.amount / 100).toFixed(2)}
                            </Typography>
                          )}
                        </Box>

                        <Typography variant="body2" fontSize="0.8rem" color={Colors.info} sx={{ mt: "4px" }}>
                          {fee.providerName}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: "8px",
                            pt: "8px",
                            borderTop: "1px solid rgba(255, 255, 255, 0.06)",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <CreditCard sx={{ fontSize: "14px", color: "rgba(255, 255, 255, 0.4)" }} />
                            <Typography variant="body2" fontSize="0.75rem" sx={{ color: "rgba(255, 255, 255, 0.5)" }}>
                              •••• {fee.cardLast4}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontSize="0.75rem" sx={{ color: "rgba(255, 255, 255, 0.5)" }}>
                            {DateTime.fromISO(fee.createdAt).toLocaleString(DateTime.DATE_MED)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: Colors.info }}>
                    No late cancellation fees
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Right Content Area */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                minHeight: 0,
                overflow: "hidden",
                height: { xs: "auto", lg: rightColumnHeight ? `${rightColumnHeight}px` : "auto" },
              }}
            >
              {hasWebptIntegration && (
                <Box sx={{ ...cardStyles, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: "16px",
                      marginBottom: "16px",
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "#FFF", fontWeight: 600 }}>
                      Plans of Care
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Tooltip
                        title={
                          planOfCareBookingUniqueId
                            ? "Open the plan of care booking page"
                            : "No booking link found in this patient's recent texts"
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            disableElevation
                            disabled={!planOfCareBookingUniqueId}
                            startIcon={<ArrowOutwardOutlined fontSize="small" />}
                            onClick={() => openPlanOfCareBookingPage(false)}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            Booking Page
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          planOfCareBookingUniqueId
                            ? "Open the plan of care booking page in debug mode"
                            : "No booking link found in this patient's recent texts"
                        }
                      >
                        <span>
                          <IconButton size="small" disabled={!planOfCareBookingUniqueId} onClick={() => openPlanOfCareBookingPage(true)}>
                            <BugReportIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disableElevation
                        startIcon={<Add fontSize="small" />}
                        component={Link}
                        to={`/dashboard/patients/${patientId}/plan-of-care`}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                      >
                        New Plan of Care
                      </Button>
                    </Box>
                  </Box>

                  {loadingPlansOfCare ? (
                    <LoadingWithMessage message="Loading plans of care..." size={30} />
                  ) : activePlansOfCare.length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {activePlansOfCare.map((planOfCare) => {
                        const appointmentsCompleted = planOfCare.appointmentsOccurred || 0;
                        const extraAppointments = planOfCare.totalExtraAppointments || 0;
                        const scheduledAppointments = planOfCare.appointmentsScheduled || 0;
                        const unscheduledAppointments = planOfCare.appointmentsUnscheduled || 0;
                        const unauthorizedAppointments = planOfCare.appointmentsUnauthorized || 0;
                        const totalForProgress = appointmentsCompleted + extraAppointments + scheduledAppointments + unscheduledAppointments + unauthorizedAppointments;
                        const progressPercentage = totalForProgress > 0 ? ((appointmentsCompleted + extraAppointments) / totalForProgress) * 100 : 0;
                        const totalAppointments = planOfCare.totalAppointments || planOfCare.numAppointments;
                        const authUsed = planOfCare.authorizationUsed || 0;
                        const authTotal = planOfCare.authorizationTotal || 0;
                        const hasAuthInfo = authTotal > 0 && !planOfCare.ignoreAuthorization;
                        const authPercentage = authTotal > 0 ? (authUsed / authTotal) * 100 : 0;
                        const isAuthLow = authPercentage > 80;
                        const isSending = sendingPocLink[planOfCare.activePlanOfCareId];
                        const frequencyText = planOfCare.frequency ? `${planOfCare.frequency}x/week` : `Every ${planOfCare.gap}d`;

                        return (
                          <Box
                            key={planOfCare.activePlanOfCareId}
                            component="a"
                            href={`/dashboard/patients/${patientId}/plan-of-care/${planOfCare.activePlanOfCareId}`}
                            sx={{
                              ...interactiveRowStyles,
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "12px 14px",
                              textDecoration: "none",
                              color: "inherit",
                            }}
                            onClick={(e: React.MouseEvent) => {
                              e.preventDefault();
                              navigate(`/dashboard/patients/${patientId}/plan-of-care/${planOfCare.activePlanOfCareId}`);
                            }}
                          >
                            {/* Left: Content */}
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                              }}
                            >
                              {/* Title */}
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                fontSize="0.9375rem"
                                sx={{
                                  color: "rgba(255, 255, 255, 0.95)",
                                  lineHeight: 1.3,
                                }}
                              >
                                {planOfCare.caseStudyName || planOfCare.name}
                              </Typography>

                              {/* Metadata Row */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                  marginBottom: "2px",
                                }}
                              >
                                {/* Progress Badge */}
                                {!planOfCare.discharged && (
                                  <Tooltip
                                    title={`${Math.round(progressPercentage)}% completed (${appointmentsCompleted + extraAppointments} of ${totalForProgress} appointments)`}
                                    arrow
                                    placement="top"
                                  >
                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        padding: "3px 8px",
                                        borderRadius: "5px",
                                        backgroundColor: "rgba(255, 255, 255, 0.06)",
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Box
                                        sx={{
                                          position: "relative",
                                          display: "inline-flex",
                                          width: "16px",
                                          height: "16px",
                                        }}
                                      >
                                        <CircularProgress
                                          variant="determinate"
                                          value={100}
                                          size={16}
                                          thickness={6}
                                          sx={{
                                            color: "rgba(255, 255, 255, 0.2)",
                                            position: "absolute",
                                          }}
                                        />
                                        <CircularProgress
                                          variant="determinate"
                                          value={progressPercentage}
                                          size={16}
                                          thickness={6}
                                          sx={{
                                            color: Colors.primary,
                                          }}
                                        />
                                      </Box>
                                      <Typography variant="body2" fontSize="0.75rem" sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 500 }}>
                                        {Math.round(progressPercentage)}%
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                )}

                                {planOfCare.discharged && (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "5px",
                                      padding: "3px 8px",
                                      borderRadius: "5px",
                                      backgroundColor: "rgba(255, 255, 255, 0.06)",
                                    }}
                                  >
                                    <Typography variant="body2" fontSize="0.75rem" sx={{ color: Colors.error, fontWeight: 500 }}>
                                      Discharged
                                    </Typography>
                                  </Box>
                                )}

                                {/* Frequency Badge (clickable to edit) */}
                                <Tooltip title="Click to change frequency" arrow placement="top">
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "5px",
                                      padding: "3px 8px",
                                      borderRadius: "5px",
                                      backgroundColor: "rgba(255, 255, 255, 0.06)",
                                      cursor: "pointer",
                                      transition: "all 0.15s ease",
                                      "&:hover": {
                                        backgroundColor: "rgba(255, 255, 255, 0.12)",
                                      },
                                    }}
                                    onClick={(e) => openFrequencyPopover(e, planOfCare)}
                                  >
                                    <CalendarTodayIcon sx={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.5)" }} />
                                    <Typography variant="body2" fontSize="0.75rem" sx={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 500 }}>
                                      {frequencyText}
                                    </Typography>
                                    <EditIcon sx={{ fontSize: "10px", color: "rgba(255, 255, 255, 0.3)" }} />
                                  </Box>
                                </Tooltip>

                                {/* Authorization Badge */}
                                {hasAuthInfo && (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "5px",
                                      padding: "3px 8px",
                                      borderRadius: "5px",
                                      backgroundColor: isAuthLow ? "rgba(255, 152, 0, 0.1)" : "rgba(255, 255, 255, 0.06)",
                                      border: isAuthLow ? `1px solid rgba(255, 152, 0, 0.3)` : "none",
                                    }}
                                  >
                                    <VerifiedUserIcon
                                      sx={{
                                        fontSize: "12px",
                                        color: isAuthLow ? Colors.warning : "rgba(255, 255, 255, 0.5)",
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      fontSize="0.75rem"
                                      sx={{
                                        color: isAuthLow ? Colors.warning : "rgba(255, 255, 255, 0.7)",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {authUsed}/{authTotal} visits
                                    </Typography>
                                  </Box>
                                )}

                                {/* Provider Badge (clickable to edit) */}
                                {(() => {
                                  const groupName = planOfCare.providerGroupId ? providerGroupNameMap[planOfCare.providerGroupId] : null;
                                  const providerIds = planOfCare.webptProviderIds || [];
                                  const providerNames = providerIds.map((id) => providerNameMap[id]).filter(Boolean);

                                  const badgeClickableStyles = {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    padding: "3px 8px",
                                    borderRadius: "5px",
                                    backgroundColor: "rgba(255, 255, 255, 0.06)",
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                    "&:hover": {
                                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                                      borderColor: "rgba(255, 255, 255, 0.3)",
                                    },
                                  };

                                  if (groupName) {
                                    return (
                                      <Tooltip title={`Click to change · ${providerNames.length > 0 ? providerNames.join(", ") : groupName}`} arrow placement="top">
                                        <Box sx={badgeClickableStyles} onClick={(e) => openProviderPopover(e, planOfCare)}>
                                          <GroupIcon sx={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.5)" }} />
                                          <Typography
                                            variant="body2"
                                            fontSize="0.75rem"
                                            sx={{
                                              color: "rgba(255, 255, 255, 0.7)",
                                              fontWeight: 500,
                                              maxWidth: "120px",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {groupName}
                                          </Typography>
                                          <EditIcon sx={{ fontSize: "10px", color: "rgba(255, 255, 255, 0.3)" }} />
                                        </Box>
                                      </Tooltip>
                                    );
                                  }

                                  if (providerNames.length > 0) {
                                    return (
                                      <Tooltip title={`Click to change · ${providerNames.join(", ")}`} arrow placement="top">
                                        <Box sx={badgeClickableStyles} onClick={(e) => openProviderPopover(e, planOfCare)}>
                                          <PersonIcon sx={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.5)" }} />
                                          <Typography
                                            variant="body2"
                                            fontSize="0.75rem"
                                            sx={{
                                              color: "rgba(255, 255, 255, 0.7)",
                                              fontWeight: 500,
                                            }}
                                          >
                                            {providerNames.length === 1 ? providerNames[0] : `${providerNames.length} providers`}
                                          </Typography>
                                          <EditIcon sx={{ fontSize: "10px", color: "rgba(255, 255, 255, 0.3)" }} />
                                        </Box>
                                      </Tooltip>
                                    );
                                  }

                                  // No provider assigned — show "+ Assign" badge
                                  if (providerGroupOptions.length > 0 || providerOptions.length > 0) {
                                    return (
                                      <Box
                                        sx={{
                                          ...badgeClickableStyles,
                                          border: "1px dashed rgba(255, 255, 255, 0.2)",
                                          backgroundColor: "transparent",
                                          "&:hover": {
                                            backgroundColor: "rgba(255, 255, 255, 0.06)",
                                            borderColor: "rgba(255, 255, 255, 0.35)",
                                          },
                                        }}
                                        onClick={(e) => openProviderPopover(e, planOfCare)}
                                      >
                                        <PersonIcon sx={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.35)" }} />
                                        <Typography
                                          variant="body2"
                                          fontSize="0.75rem"
                                          sx={{
                                            color: "rgba(255, 255, 255, 0.45)",
                                            fontWeight: 500,
                                          }}
                                        >
                                          Assign
                                        </Typography>
                                      </Box>
                                    );
                                  }

                                  return null;
                                })()}
                              </Box>

                              {/* Send Button */}
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disableElevation
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSendPocBookingLink(planOfCare.activePlanOfCareId);
                                }}
                                disabled={isSending || sendingPocLink[planOfCare.activePlanOfCareId]}
                                sx={{
                                  alignSelf: "flex-start",
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "0.8rem",
                                  padding: "5px 12px",
                                }}
                              >
                                {isSending ? "Sending..." : "Send booking link"}
                              </Button>
                            </Box>

                            {/* Right: Chevron - Centered Vertically */}
                            <ChevronRightIcon
                              sx={{
                                color: "rgba(255, 255, 255, 0.3)",
                                fontSize: "20px",
                                flexShrink: 0,
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: Colors.info }}>
                      No plans of care
                    </Typography>
                  )}
                </Box>
              )}

              {/* Text History Section */}
              <Box
                sx={{
                  ...cardStyles,
                  padding: "24px",
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {textThreadLoading ? (
                  <LoadingWithMessage message="Loading text history..." size={30} />
                ) : (
                  textThread && (
                    <TextThreadDisplay
                      thread={textThread}
                      agentId={agentId}
                      patientId={patientId}
                      messageContainerMaxHeight="none"
                      phoneNumber={patient.patient.phoneNumber}
                      waitlistSlotIds={slots.map((slot) => ({
                        waitlistSlotId: slot.waitlistSlotId,
                        appointmentDate: slot.waitlistRun.appointmentDate,
                      }))}
                      waitlistSlotStateChanges={waitlistSlotStateChanges}
                    />
                  )
                )}
              </Box>
            </Box>
          </Box>

          {/* Delete Button (Dev Only) */}
          {process.env.REACT_APP_ENV !== "production" && (
            <Box sx={{ display: "flex", marginTop: "40px", marginBottom: "20px" }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  deletePatientPermanently();
                  navigate(-1);
                }}
              >
                Delete Patient Permanently
              </Button>
            </Box>
          )}

          {/* Modal Sidebar */}
          <Sidebar className={updateOpen ? "open" : ""}>
            {organization?.integrations.includes(EHR.webpt) ? (
              <UpdateWebptPatientModal
                key={patient.patientId}
                open={updateOpen}
                onClose={() => setUpdateOpen(false)}
                patient={patient}
                activePlansOfCare={activePlansOfCare}
                onProviderAssigned={fetchPlansOfCare}
              />
            ) : null}
          </Sidebar>

          {/* Provider Assignment Popover */}
          <Popover
            open={Boolean(providerPopoverAnchor)}
            anchorEl={providerPopoverAnchor}
            onClose={closeProviderPopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: Colors.bg3,
                  border: `1px solid ${Colors.border}`,
                  borderRadius: "12px",
                  width: "320px",
                  mt: 0.5,
                },
              },
            }}
          >
            <Box sx={{ p: 2.5 }}>
              <Typography variant="body2" fontWeight="600" sx={{ color: "#FFF", fontSize: "13px", mb: 2 }}>
                Assign Providers
              </Typography>

              {/* Provider Group Select */}
              {providerGroupOptions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "11px", display: "block", mb: 0.5 }}>
                    Provider Group
                  </Typography>
                  <Select
                    value={editProviderGroupId}
                    onChange={(e) => handleEditProviderGroupChange(e.target.value)}
                    displayEmpty
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "8px",
                      color: "#FFF",
                      fontSize: "13px",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                    }}
                  >
                    <MenuItem value="">
                      <em style={{ color: "rgba(255, 255, 255, 0.5)" }}>None</em>
                    </MenuItem>
                    {providerGroupOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {/* Providers Multi-Select */}
              {providerOptions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "11px" }}>
                      Providers
                    </Typography>
                    {!editProviderGroupId &&
                      (editSelectedProviders.length === providerOptions.length ? (
                        <Typography
                          variant="caption"
                          onClick={() => setEditSelectedProviders([])}
                          sx={{
                            color: "rgba(255, 255, 255, 0.4)",
                            fontSize: "10px",
                            cursor: "pointer",
                            "&:hover": { color: "#FFF" },
                          }}
                        >
                          Deselect all
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          onClick={() => setEditSelectedProviders(providerOptions.map((o) => o.value))}
                          sx={{
                            color: "rgba(255, 255, 255, 0.4)",
                            fontSize: "10px",
                            cursor: "pointer",
                            "&:hover": { color: "#FFF" },
                          }}
                        >
                          Select all
                        </Typography>
                      ))}
                  </Box>
                  <Select
                    disabled={!!editProviderGroupId}
                    multiple
                    displayEmpty
                    value={editSelectedProviders}
                    onChange={(e) => setEditSelectedProviders(e.target.value as string[])}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <Typography sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "13px" }}>Select providers</Typography>;
                      }
                      return (selected as string[])
                        .map((v) => providerOptions.find((o) => o.value === v)?.label)
                        .filter(Boolean)
                        .join(", ");
                    }}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "8px",
                      color: "#FFF",
                      fontSize: "13px",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                  >
                    {providerOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Checkbox checked={editSelectedProviders.indexOf(opt.value) > -1} size="small" />
                        <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: "13px" }} />
                      </MenuItem>
                    ))}
                  </Select>
                  {editProviderGroupId && (
                    <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.35)", fontSize: "10px", mt: 0.5, display: "block" }}>
                      Managed by provider group
                    </Typography>
                  )}
                </Box>
              )}

              {/* Actions */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  onClick={closeProviderPopover}
                  size="small"
                  sx={{
                    textTransform: "none",
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "12px",
                    fontWeight: 500,
                    minWidth: "auto",
                    px: 1.5,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={saveProviderAssignment}
                  disabled={savingProviders}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 600,
                    minWidth: "auto",
                    px: 2,
                  }}
                >
                  {savingProviders ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
          </Popover>

          {/* Frequency Edit Popover */}
          <Popover
            open={Boolean(frequencyPopoverAnchor)}
            anchorEl={frequencyPopoverAnchor}
            onClose={closeFrequencyPopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: Colors.bg3,
                  border: `1px solid ${Colors.border}`,
                  borderRadius: "12px",
                  width: "240px",
                  mt: 0.5,
                },
              },
            }}
          >
            <Box sx={{ p: 2.5 }}>
              <Typography variant="body2" fontWeight="600" sx={{ color: "#FFF", fontSize: "13px", mb: 2 }}>
                Set Frequency
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "11px", display: "block", mb: 0.5 }}>
                  Visits per week
                </Typography>
                <TextField
                  type="number"
                  value={editFrequencyValue}
                  onChange={(e) => setEditFrequencyValue(Math.max(0, parseInt(e.target.value) || 0))}
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 7 }}
                  sx={{
                    "& .MuiInputBase-input": { color: "#FFF", fontSize: "13px" },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "8px",
                      "& fieldset": { borderColor: Colors.border },
                      "&:hover fieldset": { borderColor: Colors.border },
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  onClick={closeFrequencyPopover}
                  size="small"
                  sx={{
                    textTransform: "none",
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "12px",
                    fontWeight: 500,
                    minWidth: "auto",
                    px: 1.5,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={saveFrequency}
                  disabled={savingFrequency}
                  size="small"
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 600,
                    minWidth: "auto",
                    px: 2,
                  }}
                >
                  {savingFrequency ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
          </Popover>

          {/* No Bookable Visits Confirmation Dialog */}
          <Dialog
            open={showNoBookableVisitsDialog}
            onClose={handleCancelSendBookingLink}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: Colors.bg3,
                borderRadius: "16px",
                border: `1px solid ${Colors.secondaryDark}`,
              },
            }}
          >
            <DialogTitle sx={{ color: "#FFF", fontWeight: 600, fontSize: "16px" }}>No Bookable Visits</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>
                This patient does not have any bookable visits remaining. Do you still want to send the booking link?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ padding: "12px", borderTop: `1px solid ${Colors.border}` }}>
              <Button
                onClick={handleCancelSendBookingLink}
                sx={{
                  textTransform: "none",
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmSendBookingLink}
                disabled={pendingPlanOfCareId ? sendingPocLink[pendingPlanOfCareId] : false}
                sx={{
                  textTransform: "none",
                  backgroundColor: Colors.primary,
                  color: "#FFF",
                  fontSize: "13px",
                  fontWeight: 600,
                  px: 2.5,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: Colors.primary,
                    opacity: 0.9,
                    boxShadow: "none",
                  },
                }}
              >
                {pendingPlanOfCareId && sendingPocLink[pendingPlanOfCareId] ? "Sending..." : "Send Anyway"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Archive Patient Confirmation Dialog */}
          <Dialog
            open={showArchiveDialog}
            onClose={() => !archivingPatient && setShowArchiveDialog(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: Colors.bg3,
                borderRadius: "16px",
                border: `1px solid ${Colors.secondaryDark}`,
              },
            }}
          >
            <DialogTitle sx={{ color: "#FFF", fontWeight: 600, fontSize: "16px" }}>Archive Patient</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>
                Are you sure you want to archive this patient? They will be moved to the archived patients list.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ padding: "12px", borderTop: `1px solid ${Colors.border}` }}>
              <Button
                onClick={() => setShowArchiveDialog(false)}
                disabled={archivingPatient}
                sx={{
                  textTransform: "none",
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleArchivePatient}
                disabled={archivingPatient}
                sx={{
                  textTransform: "none",
                  backgroundColor: Colors.error,
                  color: "#FFF",
                  fontSize: "13px",
                  fontWeight: 600,
                  px: 2.5,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: Colors.error,
                    opacity: 0.9,
                    boxShadow: "none",
                  },
                }}
              >
                {archivingPatient ? "Archiving..." : "Archive"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
      {!loadingPatients && !patient && (
        <Typography variant="h5" sx={{ color: "#FFF", textAlign: "center", marginTop: "40px" }}>
          Patient not found
        </Typography>
      )}
    </Box>
  );
};

export default PatientInfoPage;
